import { useState } from 'react';
import type { CopyMode, CopyScreenshotsResult } from '@/api/play';
import { Button } from '../Button';
import { ConfirmModal } from './ConfirmModal';

/**
 * Moving a set between locales without uploading it again: pull another
 * locale's screenshots into this one, or push this one's to every locale. The
 * server does the copying (Play drafts and the Share tab alike); this is only
 * the two controls and the report.
 */
export function CopyScreenshotsBar({ lang, sources, ownCount, otherLocales, defaultMode, onCopy, onDone }: {
  lang: string;
  /** Other locales that have screenshots to copy from, with how many. */
  sources: { lang: string; count: number }[];
  /** Screenshots this locale has — nothing to push when 0. */
  ownCount: number;
  /** How many other locales "all" would write to. */
  otherLocales: number;
  defaultMode: CopyMode;
  onCopy: (input: { sourceLang: string; targetLangs: string[] | null; mode: CopyMode }) => Promise<CopyScreenshotsResult>;
  onDone: () => void;
}) {
  const [from, setFrom] = useState('');
  const [mode, setMode] = useState<CopyMode>(defaultMode);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const source = sources.some((s) => s.lang === from) ? from : '';

  const run = async (input: { sourceLang: string; targetLangs: string[] | null }) => {
    setBusy(true); setError(null); setNote(null);
    try {
      const out = await onCopy({ ...input, mode });
      const added = out.targets.reduce((n, t) => n + t.added, 0);
      const skipped = out.targets.reduce((n, t) => n + t.skipped, 0);
      setNote(input.targetLangs
        ? `Copied ${added} screenshot(s) from ${input.sourceLang}${skipped ? ` — ${skipped} skipped (already there or over 8)` : ''}.`
        : `Copied to ${out.targets.length} locale(s): ${added} screenshot(s)${skipped ? `, ${skipped} skipped` : ''}.`);
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Copy failed');
    } finally {
      setBusy(false);
      setConfirmAll(false);
    }
  };

  if (sources.length === 0 && (ownCount === 0 || otherLocales === 0)) return null;
  const selectCls = 'px-2 py-1 rounded bg-background border border-border text-content text-xs';

  return (
    <div className="mb-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
        {sources.length > 0 && (
          <>
            <span>Copy from</span>
            <select className={selectCls} value={source} disabled={busy} onChange={(e) => setFrom(e.target.value)}>
              <option value="">— locale —</option>
              {sources.map((s) => <option key={s.lang} value={s.lang}>{s.lang} · {s.count}</option>)}
            </select>
          </>
        )}
        <select className={selectCls} value={mode} disabled={busy} onChange={(e) => setMode(e.target.value as CopyMode)}>
          <option value="replace">replacing the set</option>
          <option value="append">added after the set</option>
        </select>
        {sources.length > 0 && (
          <Button size="sm" variant="secondary" disabled={!source || busy} loading={busy && !confirmAll}
            onClick={() => run({ sourceLang: source, targetLangs: [lang] })}>
            Copy into {lang}
          </Button>
        )}
        {ownCount > 0 && otherLocales > 0 && (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setConfirmAll(true)}>
            Copy {lang} to all locales ({otherLocales})
          </Button>
        )}
      </div>
      {note && <p className="text-xs text-green-500">{note}</p>}
      {error && <p className="text-xs text-red-400">{error}</p>}
      <ConfirmModal
        isOpen={confirmAll}
        title={`Copy ${lang} screenshots to all locales`}
        confirmLabel="Copy"
        danger={mode === 'replace'}
        loading={busy}
        message={mode === 'replace'
          ? `Every other locale (${otherLocales}) gets the ${ownCount} ${lang} screenshot(s) in place of its own set.`
          : `The ${ownCount} ${lang} screenshot(s) are added after each other locale's own set (${otherLocales}), up to 8, skipping ones already there.`}
        onConfirm={() => run({ sourceLang: lang, targetLangs: null })}
        onClose={() => setConfirmAll(false)}
      />
    </div>
  );
}
